import { useIsFetching, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Fragment } from "react";
import { z } from "zod";

import type { AuditChange, AuditEntry, AuditSection } from "@ravnar/api-contract";

import { ApiError } from "../../api/client";
import { auditLogQueryOptions, companyIdentityQueryOptions } from "../../api/settings";
import { AuditActor, AuditDetail, AuditSectionLabel } from "../../components/audit";
import { PageHeader } from "../../components/shell/app-shell";
import { buttonClassName } from "../../components/ui/button-styles";
import { Card, CardHeader } from "../../components/ui/card";
import { Empty } from "../../components/ui/empty";
import { FilterPill, Toolbar } from "../../components/ui/filter-pill";
import { Pager } from "../../components/ui/pager";
import { Pill } from "../../components/ui/pill";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import { AUDIT_SECTION_DOT_CLASS, AUDIT_SECTION_LABELS, AUDIT_SECTIONS } from "../../domain/settings";
import { formatDateTime, formatDay, formatTime } from "../../lib/format";

const auditLogSearchSchema = z.object({
	section: z.enum(AUDIT_SECTIONS).optional().catch(undefined),
	/** A page link from the API, which the page never reads; the newest page when there is none. */
	page: z.string().optional().catch(undefined),
	/** The id of the entry whose values are unfolded, if any. */
	entry: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/settings/audit-log")({
	validateSearch: auditLogSearchSchema,
	loaderDeps: ({ search: { section, page } }) => ({ section, page }),
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: async ({ context, deps }) => {
		try {
			await Promise.all([
				context.queryClient.query({ ...auditLogQueryOptions(deps), staleTime: "static" }),
				context.queryClient.query({ ...companyIdentityQueryOptions(), staleTime: "static" }),
			]);
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the newest page.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				throw redirect({ to: "/settings/audit-log", search: { section: deps.section }, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "Audit log · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Audit log"] },
	component: AuditLogPage,
});

/** The values an entry recorded: what each one is now, and what it was before. */
function EntryValues({ changes }: { readonly changes: readonly AuditChange[] }) {
	return (
		<dl className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-field border border-line bg-bg px-3 py-2.5">
			{changes.map(change => (
				<Fragment key={change.label}>
					<dt className="text-caption text-ink-3">{change.label}</dt>
					<dd className="text-caption text-ink">
						{change.value === "" ? "—" : change.value}
						{change.previous === undefined || change.previous === change.value ? null : (
							<span className="text-ink-3">
								{" · was "}
								{change.previous === "" ? "empty" : change.previous}
							</span>
						)}
					</dd>
				</Fragment>
			))}
		</dl>
	);
}

function AuditLogPage() {
	const { section, page, entry: openEntryId } = Route.useSearch();
	const navigate = Route.useNavigate();
	const auditLogQuery = auditLogQueryOptions({ section, page });
	const { data: auditLog } = useSuspenseQuery(auditLogQuery);
	const { data: identity } = useSuspenseQuery(companyIdentityQueryOptions());
	const isUpdating = useIsFetching({ queryKey: auditLogQuery.queryKey, exact: true }) > 0;

	/** Shows a section, or every entry, from its newest page. */
	function showSection(option: AuditSection | undefined) {
		void navigate({ search: { section: option }, replace: true });
	}

	/** Unfolds an entry's values, or folds them away again. The open entry stays in the URL, so it survives a reload. */
	function toggleEntry(id: string) {
		void navigate({
			search: previous => ({ ...previous, entry: previous.entry === id ? undefined : id }),
			replace: true,
		});
	}

	const columns: readonly TableColumn<AuditEntry>[] = [
		{
			key: "at",
			header: "Time",
			muted: true,
			cell: entry => <time dateTime={entry.at}>{formatTime(entry.at, identity.timeZone)}</time>,
		},
		{
			key: "action",
			header: "Event",
			cell: entry => (
				<>
					<span className="flex flex-wrap items-center gap-2">
						<span className="font-medium">{entry.action}</span>
						{entry.run?.outcome === "failed" ? <Pill tone="alert">Failed</Pill> : null}
					</span>
					<AuditDetail detail={entry.detail} subject={entry.subject} />
				</>
			),
		},
		{ key: "section", header: "Section", cell: entry => <AuditSectionLabel category={entry.category} /> },
		{ key: "actor", header: "Actor", align: "right", cell: entry => <AuditActor actor={entry.actor} /> },
	];
	// A section's own page lists nothing but that section, which its card's title already names.
	const shownColumns = section === undefined ? columns : columns.filter(column => column.key !== "section");

	return (
		<>
			<PageHeader
				title="Audit log"
				description="Everything that changed in the organization, newest first, day by day: who signed in, what each person changed, and what Ravnar's agents did on their own, with the tokens each run took. Entries cannot be edited or deleted."
			/>
			<Toolbar>
				<FilterPill
					active={section === undefined}
					onClick={() => {
						showSection(undefined);
					}}
				>
					All
				</FilterPill>
				{AUDIT_SECTIONS.map(option => (
					<FilterPill
						key={option}
						active={section === option}
						dotClassName={AUDIT_SECTION_DOT_CLASS[option]}
						onClick={() => {
							showSection(section === option ? undefined : option);
						}}
					>
						{AUDIT_SECTION_LABELS[option]}
					</FilterPill>
				))}
			</Toolbar>
			<Card>
				<CardHeader
					title={section === undefined ? "All entries" : AUDIT_SECTION_LABELS[section]}
					action={
						<span className="flex items-center gap-3">
							<Updating active={isUpdating} />
							<span className="text-caption text-ink-3">Times in {identity.timeZone}</span>
						</span>
					}
				/>
				{auditLog.entries.length > 0 ? (
					<Table
						label="Audit log"
						columns={shownColumns}
						rows={auditLog.entries}
						rowKey={entry => entry.id}
						groupBy={entry => formatDay(entry.at, identity.timeZone)}
						expansion={{
							toggleLabel: entry =>
								`Values of ${entry.action}, ${formatDateTime(entry.at, identity.timeZone)}`,
							isOpen: entry => entry.id === openEntryId,
							onToggle: entry => {
								toggleEntry(entry.id);
							},
							panel: entry =>
								entry.changes === undefined || entry.changes.length === 0 ? null : (
									<EntryValues changes={entry.changes} />
								),
						}}
					/>
				) : (
					<Empty>{section === undefined ? "No entries yet." : "No entries in this section yet."}</Empty>
				)}
				{auditLog.next === null && auditLog.previous === null ? null : (
					<Pager
						label="Audit log pages"
						previous={
							auditLog.previous === null ? null : (
								<Link
									to="/settings/audit-log"
									search={{ section, page: auditLog.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							auditLog.next === null ? null : (
								<Link
									to="/settings/audit-log"
									search={{ section, page: auditLog.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
		</>
	);
}
